import { BaseApiClient } from "../api/baseApiClient.service";
import type { SystemRole } from "../auth/auth.types";

/**
 * User with balance information
 */
export interface UserWithBalance {
	id: string;
	email: string;
	name: string | null;
	coinBalance: number;
	systemRoles: SystemRole[];
	createdAt: string;
	lastActive: string | null;
	podcastCount: number;
	transactionCount: number;
	currentPlanName: string;
	lastBillingReset: string | null;
	subscriptionEndsAt: string | null;
}

/**
 * Platform statistics
 */
export interface PlatformStats {
	totalUsers: number;
	totalCoinsInCirculation: number;
	totalTransactions: number;
	totalPodcasts: number;
	averageBalancePerUser: number;
	activeUsersLast7Days: number;
	activeUsersLast30Days: number;
}

/**
 * User details
 */
export interface UserDetails {
	user: {
		id: string;
		email: string;
		name: string | null;
		coinBalance: number;
		systemRoles: SystemRole[];
		createdAt: string;
		lastActive: string | null;
		currentPlanId: string;
	};
	balance: number;
	stats: {
		totalSpent: number;
		totalEarned: number;
		transactionCount: number;
		podcastCount: number;
		workspaceCount: number;
	};
	recentTransactions: any[];
	subscription: {
		planName: string;
		status: string;
		endsAt: string | null;
		limits: {
			monthlyCoins: number;
			maxChats: number;
			maxStorageEpisodes: number;
			maxUploadMb?: number; // Optional field not in all plans
			features: {
				chapterSharing: boolean;
				wholePodcastSharing: boolean;
				batchCreation: boolean;
				canGenerate: boolean;
				retainAccess: boolean;
			};
		};
	};
}

/**
 * Service for platform manager operations
 */
export class PlatformManagerService {
	private static instance: PlatformManagerService;
	private apiClient: BaseApiClient;

	private constructor() {
		this.apiClient = BaseApiClient.getInstance();
	}

	static getInstance(): PlatformManagerService {
		if (!PlatformManagerService.instance) {
			PlatformManagerService.instance = new PlatformManagerService();
		}
		return PlatformManagerService.instance;
	}

	/**
	 * Get all users with balance information
	 */
	async getAllUsers(): Promise<UserWithBalance[]> {
		const response = await this.apiClient.get<{
			success: boolean;
			users: UserWithBalance[];
			total: number;
		}>("/platform-manager/users");

		if (!response.success) {
			throw new Error("Failed to fetch users");
		}

		return response.users;
	}

	/**
	 * Get detailed information about a specific user
	 */
	async getUserDetails(userId: string): Promise<UserDetails> {
		const response = await this.apiClient.get<
			{
				success: boolean;
			} & UserDetails
		>(`/platform-manager/users/${userId}`);

		if (!response.success) {
			throw new Error("Failed to fetch user details");
		}

		return response;
	}

	/**
	 * Add coins to a user's balance
	 */
	async addCoins(
		userId: string,
		amount: number,
		description?: string,
	): Promise<void> {
		const response = await this.apiClient.post<
			{
				success: boolean;
				transaction: any;
				newBalance: number;
			},
			{ amount: number; description?: string }
		>(`/platform-manager/users/${userId}/coins/add`, {
			amount,
			description,
		});

		if (!response.success) {
			throw new Error("Failed to add coins");
		}
	}

	/**
	 * Deduct coins from a user's balance
	 */
	async deductCoins(
		userId: string,
		amount: number,
		description?: string,
	): Promise<void> {
		const response = await this.apiClient.post<
			{
				success: boolean;
				transaction: any;
				newBalance: number;
			},
			{ amount: number; description?: string }
		>(`/platform-manager/users/${userId}/coins/deduct`, {
			amount,
			description,
		});

		if (!response.success) {
			throw new Error("Failed to deduct coins");
		}
	}

	/**
	 * Get platform-wide statistics
	 */
	async getPlatformStats(): Promise<PlatformStats> {
		const response = await this.apiClient.get<{
			success: boolean;
			stats: PlatformStats;
		}>("/platform-manager/stats");

		if (!response.success) {
			throw new Error("Failed to fetch platform stats");
		}

		return response.stats;
	}

	/**
	 * Get all transactions
	 */
	async getTransactions(params?: {
		limit?: number;
		offset?: number;
		userId?: string;
		startDate?: Date;
		endDate?: Date;
	}): Promise<any[]> {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.offset) queryParams.append("offset", params.offset.toString());
		if (params?.userId) queryParams.append("userId", params.userId);
		if (params?.startDate)
			queryParams.append("startDate", params.startDate.toISOString());
		if (params?.endDate)
			queryParams.append("endDate", params.endDate.toISOString());

		const response = await this.apiClient.get<{
			success: boolean;
			transactions: any[];
		}>(`/platform-manager/transactions?${queryParams.toString()}`);

		if (!response.success) {
			throw new Error("Failed to fetch transactions");
		}

		return response.transactions;
	}

	/**
	 * Get all subscription plans
	 */
	async getAllSubscriptionPlans(): Promise<SubscriptionPlan[]> {
		const response = await this.apiClient.get<{
			success: boolean;
			plans: SubscriptionPlan[];
		}>("/platform-manager/subscriptions");

		if (!response.success) {
			throw new Error("Failed to fetch subscription plans");
		}

		return response.plans;
	}

	/**
	 * Get Stripe prices for a product ID
	 */
	async getStripePricesForProduct(productId: string): Promise<
		Array<{
			id: string;
			unitAmount: number | null;
			currency: string;
			type: "one_time" | "recurring";
			recurring?: {
				interval: string;
				intervalCount: number;
			} | null;
			nickname?: string | null;
			active: boolean;
		}>
	> {
		console.log(
			"[PlatformManagerService] Fetching prices for product:",
			productId,
		);
		const response = await this.apiClient.get<{
			success: boolean;
			prices: Array<{
				id: string;
				unitAmount: number | null;
				currency: string;
				type: "one_time" | "recurring";
				recurring?: {
					interval: string;
					intervalCount: number;
				} | null;
				nickname?: string | null;
				active: boolean;
			}>;
		}>(`/platform-manager/stripe/prices/${productId}`);

		console.log("[PlatformManagerService] API response:", response);

		if (!response.success) {
			console.error(
				"[PlatformManagerService] API returned success: false",
				response,
			);
			throw new Error("Failed to fetch Stripe prices");
		}

		console.log("[PlatformManagerService] Returning prices:", response.prices);
		return response.prices;
	}

	/**
	 * Update a subscription plan's display fields
	 */
	async updateSubscriptionPlan(
		planId: string,
		updates: {
			description?: Record<string, string> | null;
			features?: Record<string, string[]> | null;
			highlight?: boolean;
			cta?: Record<string, string> | null;
			price?: number | null;
			originalPrice?: number | null;
			stripeProductId?: string | null;
			stripePriceId?: string | null;
			showInFrontend?: boolean;
		},
	): Promise<SubscriptionPlan> {
		const response = await this.apiClient.patch<
			{
				success: boolean;
				plan: SubscriptionPlan;
				message: string;
			},
			{
				description?: Record<string, string> | null;
				features?: Record<string, string[]> | null;
				highlight?: boolean;
				cta?: Record<string, string> | null;
				price?: number | null;
				originalPrice?: number | null;
				stripeProductId?: string | null;
				stripePriceId?: string | null;
				showInFrontend?: boolean;
			}
		>(`/platform-manager/subscriptions/${planId}`, updates);

		if (!response.success) {
			throw new Error("Failed to update subscription plan");
		}

		return response.plan;
	}

	/**
	 * Upgrade a user's subscription plan
	 */
	async upgradeUserSubscription(userId: string, planId: string): Promise<void> {
		const response = await this.apiClient.post<
			{
				success: boolean;
				message: string;
			},
			{ planId: string }
		>(`/platform-manager/users/${userId}/subscription/upgrade`, {
			planId,
		});

		if (!response.success) {
			throw new Error("Failed to upgrade subscription");
		}
	}

	/**
	 * Manually reset a user's billing cycle
	 */
	async resetUserBillingCycle(userId: string): Promise<void> {
		const response = await this.apiClient.post<
			{
				success: boolean;
				message: string;
			},
			Record<string, never>
		>(`/platform-manager/users/${userId}/subscription/reset-billing`, {});

		if (!response.success) {
			throw new Error("Failed to reset billing cycle");
		}
	}
}

/**
 * Stable tier slug derived server-side from the plan name.
 * Use this for product logic instead of matching the display name.
 */
export type PlanTier = "FREE" | "STARTER" | "CREATOR" | "DOWNSWELL" | "UNKNOWN";

/**
 * Subscription plan interface
 */
export interface SubscriptionPlan {
	id: string;
	name: string;
	/** Stable identifier — present on every plan returned by /subscription/plans. */
	tier: PlanTier;
	stripeProductId: string;
	stripePriceId?: string | null;
	description?: Record<string, string> | null; // Multilingual: { "en": "...", "de": "..." }
	features?: Record<string, string[]> | null; // Multilingual: { "en": ["..."], "de": ["..."] }
	highlight?: boolean;
	cta?: Record<string, string> | null; // Multilingual: { "en": "...", "de": "..." }
	price?: number | null;
	originalPrice?: number | null;
	launchOffer?: Record<string, string> | null; // Multilingual: { "en": "...", "de": "..." }
	showInFrontend?: boolean;
	limits: {
		monthlyCoins: number;
		maxChats: number;
		maxStorageEpisodes: number;
		features: {
			chapterSharing: boolean;
			wholePodcastSharing: boolean;
			batchCreation: boolean;
			retainAccess: boolean;
		};
	};
	createdAt: string;
	updatedAt: string;
}
